import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService, RegisterRequest } from './auth.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { PASSWORD_POLICY_HINT, passwordPolicyValidator } from './password-policy';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="auth-container">
      <div class="scryber-icon-row">
        <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
      </div>
      <div class="auth-card">
        <div class="auth-header">
          <h1>Create your Scryber account</h1>
          <p class="auth-subtitle">Sign up to access your medical transcription dashboard</p>
        </div>

        @if (errorMessage) {
          <div class="auth-error" role="alert">
            {{ errorMessage }}
          </div>
        }

        <form [formGroup]="form" (ngSubmit)="onSubmit()" class="auth-form">
          <div class="form-row">
            <div class="form-group">
              <label for="firstName">First name <span class="required">*</span></label>
              <input id="firstName" type="text" formControlName="firstName" placeholder="First name" autocomplete="given-name" />
              @if (form.get('firstName')?.invalid && form.get('firstName')?.touched) {
                <span class="field-error">First name is required</span>
              }
            </div>
            <div class="form-group">
              <label for="lastName">Last name <span class="required">*</span></label>
              <input id="lastName" type="text" formControlName="lastName" placeholder="Last name" autocomplete="family-name" />
              @if (form.get('lastName')?.invalid && form.get('lastName')?.touched) {
                <span class="field-error">Last name is required</span>
              }
            </div>
          </div>

          <div class="form-group">
            <label for="salutation">Title</label>
            <input id="salutation" type="text" formControlName="salutation" placeholder="MD, NP, etc." autocomplete="honorific-prefix" />
          </div>

          <div class="form-group">
            <label for="institutionName">Institution / practice name</label>
            <input id="institutionName" type="text" formControlName="institutionName" placeholder="Institution or practice name" />
          </div>

          <div class="form-group">
            <label for="email">Email <span class="required">*</span></label>
            <input id="email" type="email" formControlName="email" placeholder="you@example.com" autocomplete="email" />
            @if (form.get('email')?.invalid && form.get('email')?.touched) {
              <span class="field-error">Valid email is required</span>
            }
          </div>

          <div class="form-group">
            <label for="password">Password <span class="required">*</span></label>
            <input id="password" type="password" formControlName="password" placeholder="••••••••" autocomplete="new-password" />
            @if (form.get('password')?.touched) {
              @if (form.get('password')?.hasError('required')) {
                <span class="field-error">Password is required</span>
              } @else if (form.get('password')?.hasError('passwordPolicy')) {
                <span class="field-error">{{ passwordPolicyHint }}</span>
              }
            }
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm password <span class="required">*</span></label>
            <input id="confirmPassword" type="password" formControlName="confirmPassword" placeholder="••••••••" autocomplete="new-password" />
            @if (form.get('confirmPassword')?.touched) {
              @if (form.get('confirmPassword')?.hasError('required')) {
                <span class="field-error">Please confirm your password</span>
              } @else if (form.get('confirmPassword')?.hasError('passwordPolicy')) {
                <span class="field-error">{{ passwordPolicyHint }}</span>
              }
            }
            @if (
              form.errors?.['passwordMismatch'] &&
              form.get('password')?.value &&
              form.get('confirmPassword')?.value &&
              (form.get('confirmPassword')?.touched || form.get('password')?.touched)
            ) {
              <span class="field-error">Passwords do not match</span>
            }
          </div>

          <button type="submit" class="auth-button primary" [disabled]="form.invalid || loading">
            {{ loading ? 'Creating account…' : 'Create account' }}
            <span class="btn-arrow">→</span>
          </button>
        </form>

        <div class="auth-footer">
          <p>Already have an account? <button type="button" class="auth-link auth-link-button" (click)="goToLogin()">Sign in</button></p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100vh;
      background: linear-gradient(160deg, var(--scryber-rail-from) 0%, var(--scryber-rail-to) 55%, #022c22 100%);
      padding: 0 0 20px;
    }
    .auth-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 40px rgba(6, 78, 59, 0.25);
      padding: 40px;
      width: calc(100% - 40px);
      max-width: 560px;
      margin: auto;
    }
    .auth-header h1 {
      color: #2d3748;
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 8px 0;
    }
    .auth-subtitle {
      color: #718096;
      font-size: 14px;
      margin: 0 0 24px 0;
      line-height: 1.5;
    }
    .auth-error {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #b91c1c;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .auth-form {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .form-group label {
      color: #374151;
      font-size: 14px;
      font-weight: 500;
    }
    .required { color: #b91c1c; }
    .form-group input {
      padding: 10px 12px;
      font-size: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      background: white;
      color: #1f2937;
    }
    .form-group input::placeholder { color: #9ca3af; }
    .form-group input:focus {
      outline: none;
      border-color: var(--scryber-green-600);
      box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.25);
    }
    .field-error {
      font-size: 12px;
      color: #b91c1c;
    }
    .auth-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: 8px;
      touch-action: manipulation;
      -webkit-tap-highlight-color: rgba(5, 150, 105, 0.15);
    }
    .auth-button.primary {
      background: linear-gradient(135deg, var(--scryber-green-600) 0%, var(--scryber-rail-to) 100%);
      color: white;
    }
    .auth-button.primary:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(5, 150, 105, 0.4);
    }
    .auth-button:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
    .btn-arrow { font-size: 18px; }
    .auth-footer {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
    }
    .auth-footer p {
      color: #718096;
      font-size: 14px;
      margin: 0;
    }
    .auth-link {
      color: var(--scryber-green-600);
      font-weight: 500;
      text-decoration: none;
      touch-action: manipulation;
    }
    .auth-link:hover { text-decoration: underline; }
    .auth-link-button {
      background: none;
      border: none;
      padding: 0;
      font: inherit;
      cursor: pointer;
      -webkit-tap-highlight-color: rgba(5, 150, 105, 0.15);
    }
    .auth-footer .auth-link-button {
      display: inline;
      min-height: 44px;
      line-height: 1.4;
      vertical-align: baseline;
    }
    @media (max-width: 480px) {
      .auth-card { padding: 24px; }
      .form-row { grid-template-columns: 1fr; }
    }
  `],
})
export class SignupComponent implements OnInit, OnDestroy {
  readonly passwordPolicyHint = PASSWORD_POLICY_HINT;
  form: FormGroup;
  loading = false;
  errorMessage = '';
  private formChangesSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group(
      {
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        salutation: [''],
        institutionName: [''],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, passwordPolicyValidator()]],
        confirmPassword: ['', [Validators.required, passwordPolicyValidator()]],
      },
      { validators: signupPasswordsMatchValidator }
    );
  }

  ngOnInit(): void {
    this.formChangesSub = this.form.valueChanges.subscribe(() => this.cdr.markForCheck());
    this.formChangesSub.add(this.form.statusChanges.subscribe(() => this.cdr.markForCheck()));
  }

  ngOnDestroy(): void {
    this.formChangesSub?.unsubscribe();
  }

  goToLogin(): void {
    void this.router.navigate(['/login']);
  }

  onSubmit(): void {
    if (this.form.invalid || this.loading) return;
    this.errorMessage = '';
    this.loading = true;
    this.cdr.markForCheck();
    const value = this.form.getRawValue();
    const req: RegisterRequest = {
      email: value.email,
      password: value.password,
      firstName: value.firstName,
      lastName: value.lastName,
      salutation: value.salutation || undefined,
      institutionName: value.institutionName || undefined,
    };
    this.auth.register(req).subscribe({
      next: () => this.router.navigate(['/']),
      error: (err) => {
        this.loading = false;
        const body = err && (err as any).error;
        const msg =
          (body && typeof body === 'object' && (body.message || body.error)) ||
          (typeof body === 'string' ? body : null) ||
          err?.message;
        this.errorMessage = msg && String(msg).trim() ? String(msg).trim() : 'Registration failed. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }
}

/** Requires password and confirm password to be identical when both are non-empty. */
const signupPasswordsMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const p = group.get('password')?.value;
  const c = group.get('confirmPassword')?.value;
  if (p == null && c == null) {
    return null;
  }
  if (typeof p === 'string' && typeof c === 'string' && p.length > 0 && c.length > 0 && p !== c) {
    return { passwordMismatch: true };
  }
  return null;
};
